import { useState, useRef, useEffect } from 'react';
import { CountryOverview, CardStatus } from '../hooks/useAllCountries';
import styles from './CountryCard.module.css';

import { flagEmoji } from '../utils/flag';
import { resolveTimeZone } from '../utils/timezone';

function useLocalTime(timezone: string): string {
  const [time, setTime] = useState('');
  useEffect(() => {
    function update() {
      try {
        setTime(
          new Date().toLocaleTimeString('en-US', {
            timeZone: resolveTimeZone(timezone),
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        );
      } catch {
        setTime('--:--');
      }
    }
    update();
    const id = setInterval(update, 30_000);
    return () => clearInterval(id);
  }, [timezone]);
  return time;
}

function tzAbbr(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: resolveTimeZone(timezone), timeZoneName: 'short' })
      .formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? timezone;
  } catch {
    return timezone;
  }
}

interface Props {
  overview: CountryOverview;
  selected: boolean;
  onClick: () => void;
  onConfirm: () => void;
  onAdjust: () => void;
  onHistory: () => void;
  onInitialize: () => void;
}

export default function CountryCard({
  overview,
  selected,
  onClick,
  onConfirm,
  onAdjust,
  onHistory,
  onInitialize,
}: Props) {
  const { location, status, cardStatus, tomorrowMessage, pendingDetections } = overview;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const localTime = useLocalTime(location.timezone);
  const flag = flagEmoji(location.code);
  const day = status?.date.day ?? null;

  useEffect(() => {
    if (!menuOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  const statusLabel: Record<CardStatus, string> = {
    gray: 'Not initialized',
    white: 'Stable',
    yellow: 'Needs confirmation',
    green: 'Confirmed',
    red: 'Inconsistent',
  };

  return (
    <div
      className={`${styles.card} ${styles[cardStatus]} ${selected ? styles.selected : ''}`}
      onClick={onClick}
    >
      {/* Header row */}
      <div className={styles.header}>
        <span className={styles.flag}>{flag}</span>
        <div className={styles.nameBlock}>
          <span className={styles.name}>{location.name}</span>
          <span className={styles.tz}>{tzAbbr(location.timezone)} · {localTime}</span>
        </div>
        <div className={styles.menuWrap} ref={menuRef}>
          <button
            className={styles.menuBtn}
            onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            aria-label="More actions"
          >
            <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
              <circle cx="8" cy="3" r="1.5" />
              <circle cx="8" cy="8" r="1.5" />
              <circle cx="8" cy="13" r="1.5" />
            </svg>
          </button>
          {menuOpen && (
            <div className={styles.menu}>
              <button className={styles.menuItem} onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onAdjust(); }}>
                Adjust Date
              </button>
              <button className={styles.menuItem} onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onHistory(); }}>
                View History
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Date row */}
      <div className={styles.dateRow}>
        {day !== null && day > 0 ? (
          <>
            <span className={styles.hijriDay}>Day {day}</span>
            <span className={styles.hijriMonth}>{status?.date.month.en} {status?.date.year}</span>
          </>
        ) : cardStatus === 'gray' ? (
          <span className={styles.hijriDay}>Not initialized</span>
        ) : (
          <span className={styles.hijriDay}>Day unknown</span>
        )}
        {status?.gregorianDate && (
          <span className={styles.gregorian}>{status.gregorianDate}</span>
        )}
      </div>

      {/* Status badge */}
      <div className={styles.statusRow}>
        <span className={`${styles.badge} ${styles[`badge_${cardStatus}`]}`}>
          {statusLabel[cardStatus]}
        </span>
        {pendingDetections > 0 && (
          <span className={styles.detectionsBadge}>{pendingDetections} detection{pendingDetections > 1 ? 's' : ''}</span>
        )}
      </div>

      {/* Green: tomorrow message */}
      {cardStatus === 'green' && tomorrowMessage && (
        <p className={styles.tomorrowMsg}>{tomorrowMessage}</p>
      )}

      {/* CTAs */}
      <div className={styles.ctaRow} onClick={(e) => e.stopPropagation()}>
        {cardStatus === 'gray' && (
          <button className={`${styles.ctaBtn} ${styles.ctaInit}`} onClick={onInitialize}>
            Initialize
          </button>
        )}
        {cardStatus === 'yellow' && (
          <button className={`${styles.ctaBtn} ${styles.ctaConfirm}`} onClick={onConfirm}>
            Confirm Month End
          </button>
        )}
        {cardStatus === 'red' && (
          <button className={`${styles.ctaBtn} ${styles.ctaFix}`} onClick={onAdjust}>
            Fix Issue
          </button>
        )}
      </div>
    </div>
  );
}
